import { useCallback, useMemo } from 'react';
import { noop } from 'lodash';
import type { WatchQueryFetchPolicy } from '@apollo/client';
import { useCursorPagination } from '@frameio/graphql-client';
import { useShareTypeFragment } from '@frameio/graphql-client/generated';
import { useActiveFolderId, useActiveShareId } from '@frameio/shared/contexts';
import { useShareAssetCounts } from '@frameio/shared/shares/hooks/useShareAssetCounts';
import { EMPTY_ARRAY } from '@frameio/utils';
import type { FinderScrollBoundaryInfo } from '../../finder';
import { useFetchAtScrollBoundary } from '../../shared/hooks/useFetchAtScrollBoundary';
import { useGetShareCollectionAssetsQuery } from '../gql/generated/GetShareCollectionAssets';

export const SHARES_CURSOR_PAGE_SIZE = 200;

type UseShareAssetsArgs = {
  fetchPolicyOptions?: {
    fetchPolicy?: WatchQueryFetchPolicy;
    nextFetchPolicy?: WatchQueryFetchPolicy;
  };
  /** Optionally provide a folder id manually */
  folderId?: string;
  /** Optionally skip using the folder id as part of the assets query */
  skipFolderId?: boolean;
};

/**
 * The useCollectionAssets hook returns the results of GetBareAssets query. This is
 * the base query used to fetch ordered asset ids.
 *
 * @returns result
 * @returns result.collectionAssets The collectionAssets query result
 * @returns result.fetchAtScrollBoundary Callback for fetching more assets based on scroll boundary
 */
export function useShareAssets({
  fetchPolicyOptions = {},
  folderId: folderIdFromProps,
  skipFolderId = false,
}: UseShareAssetsArgs = {}) {
  const shareId = useActiveShareId();
  const activeFolderId = useActiveFolderId();
  const folderId = folderIdFromProps ?? activeFolderId;

  const shareInfo = useShareTypeFragment({
    from: { __typename: 'Share', id: shareId },
  });

  const isCollectionShare = shareInfo?.data?.__typename === 'CollectionShare';

  // Kept separate so slow count aggregations don't block the asset list.
  const {
    loading: aggregatesLoading,
    fileCount,
    folderCount,
    fileSize,
    folderSize,
  } = useShareAssetCounts({
    shareId,
    folderId: !skipFolderId ? folderId : undefined,
  });

  const {
    data: shareFoldersData,
    error: shareFoldersError,
    loading: shareFoldersLoading,
    fetchNext: shareFoldersFetchNext,
  } = useCursorPagination(
    useGetShareCollectionAssetsQuery({
      variables: {
        shareId,
        folderId: !skipFolderId ? folderId : undefined,
        assetType: 'FOLDER',
        page: {
          first: SHARES_CURSOR_PAGE_SIZE,
        },
      },
      skip: !shareId || isCollectionShare,
      errorPolicy: 'all',
      ...fetchPolicyOptions,
    })
  );

  const foldersPageInfo = shareFoldersData?.share?.collectionAssets?.pageInfo;

  const {
    data: shareFilesData,
    error: shareFilesError,
    loading: shareFilesLoading,
    fetchNext: shareFilesFetchNext,
  } = useCursorPagination(
    useGetShareCollectionAssetsQuery({
      variables: {
        shareId,
        folderId: !skipFolderId ? folderId : undefined,
        assetType: 'FILE',
        page: {
          first: SHARES_CURSOR_PAGE_SIZE,
        },
      },
      skip: !shareId || (foldersPageInfo?.hasNextPage && !isCollectionShare),
      errorPolicy: 'all',
      ...fetchPolicyOptions,
    })
  );

  const filesPageInfo = shareFilesData?.share?.collectionAssets?.pageInfo;

  const fetchFoldersAtScrollBoundary = useFetchAtScrollBoundary({
    cursorPageSize: SHARES_CURSOR_PAGE_SIZE,
    fetchNext: shareFoldersFetchNext,
    pageInfo: foldersPageInfo,
  });

  const fetchFilesAtScrollBoundary = useFetchAtScrollBoundary({
    cursorPageSize: SHARES_CURSOR_PAGE_SIZE,
    fetchNext: shareFilesFetchNext,
    pageInfo: filesPageInfo,
  });

  const fetchNextPageForFiles = useCallback(() => {
    if (filesPageInfo?.hasNextPage) {
      shareFilesFetchNext({
        count: SHARES_CURSOR_PAGE_SIZE,
        pageInfo: filesPageInfo,
      });
    }
  }, [shareFilesFetchNext, filesPageInfo]);

  const folderAssets = shareFoldersData?.share?.collectionAssets;
  const fileAssets = shareFilesData?.share?.collectionAssets;

  const folderIds = useMemo(
    () => folderAssets?.nodes.map(({ id }) => id ?? '') ?? EMPTY_ARRAY,
    [folderAssets?.nodes]
  );

  const fileIds = useMemo(
    () => fileAssets?.nodes.map(({ id }) => id ?? '') ?? EMPTY_ARRAY,
    [fileAssets?.nodes]
  );

  const collectionGroups = shareFilesData?.share?.collectionAssets?.groups;

  const fetchAtScrollBoundary = useCallback(
    (input: FinderScrollBoundaryInfo) => {
      if (foldersPageInfo?.hasNextPage) {
        fetchFoldersAtScrollBoundary(input);
      }

      /**
       * Folders are always fetched first, so we can safely assume if we have not finished fetching folders that we do
       * not need to try fetching files.
       **/
      if (filesPageInfo?.hasNextPage && !foldersPageInfo?.hasNextPage) {
        fetchFilesAtScrollBoundary(input);
      }
    },
    [
      fetchFilesAtScrollBoundary,
      fetchFoldersAtScrollBoundary,
      filesPageInfo?.hasNextPage,
      foldersPageInfo?.hasNextPage,
    ]
  );

  return {
    error: shareFilesError ?? shareFoldersError,
    loading:
      (shareFoldersLoading && !shareFoldersData) ||
      (shareFilesLoading && !shareFilesData),
    foldersInitialLoading: shareFoldersLoading && !shareFoldersData,
    filesInitialLoading: shareFilesLoading && !shareFilesData,
    aggregatesLoading,
    fetchAtScrollBoundary,
    fetchNextPageForFiles,
    fileAssets,
    fileCount,
    fileIds,
    fileSize,
    folderAssets,
    folderCount,
    folderIds,
    folderSize,
    hasGroups: collectionGroups && collectionGroups.length > 0,
    isSyncing:
      (folderAssets !== undefined && shareFoldersLoading === true) ||
      (fileAssets !== undefined && shareFilesLoading === true),
    onToggleGroupCollapse: noop,
  };
}
